import React from "react";
import { NavLink } from "react-router-dom";
import {
  FaBell,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaCog,
  FaCreditCard,
  FaHome,
  FaLayerGroup,
  FaUsers,
  FaUserShield,
} from "react-icons/fa";

import { useSidebar } from "../../contexts/SidebarContext";

const ADMIN_BASE = "/appli";

const MENU_SECTIONS = [
  {
    label: "Pilotage",
    items: [
      {
        label: "Tableau de bord",
        to: `${ADMIN_BASE}/dashboard`,
        icon: FaHome,
        description: "Vue générale",
      },
      {
        label: "Notifications",
        to: `${ADMIN_BASE}/notifications`,
        icon: FaBell,
        description: "Alertes & messages système",
      },
    ],
  },
  {
    label: "Données sportives",
    items: [
      {
        label: "Utilisateurs",
        to: `${ADMIN_BASE}/utilisateurs`,
        icon: FaUserShield,
        description: "Comptes & accès",
      },
      {
        label: "Adhérents",
        to: `${ADMIN_BASE}/adherents`,
        icon: FaUsers,
        description: "Judokas & profils",
      },
      {
        label: "Groupes",
        to: `${ADMIN_BASE}/groupes`,
        icon: FaLayerGroup,
        description: "Groupes coachs",
      },
      {
        label: "Événements",
        to: `${ADMIN_BASE}/evenements`,
        icon: FaCalendarAlt,
        description: "Stages, compétitions",
      },
    ],
  },
  {
    label: "Commercialisation",
    items: [
      {
        label: "Abonnements",
        to: `${ADMIN_BASE}/abonnements`,
        icon: FaCreditCard,
        description: "Achats & renouvellements",
      },
    ],
  },
  {
    label: "Configuration",
    items: [
      {
        label: "Paramètres",
        to: `${ADMIN_BASE}/parametres`,
        icon: FaCog,
        description: "Site & application",
      },
    ],
  },
];

function AdminMenuItem({ item, isCollapsed }) {
  const Icon = item.icon;

  if (item.disabled) {
    return (
      <div
        className={`group flex min-h-12 items-center gap-3 rounded-2xl px-3 py-2 text-sm font-bold text-white/30 ${
          isCollapsed ? "justify-center" : ""
        }`}
        title={isCollapsed ? `${item.label} - à venir` : undefined}
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/[0.04] text-white/25">
          <Icon />
        </span>

        {!isCollapsed && (
          <div className="min-w-0">
            <p className="truncate">{item.label}</p>
            <p className="truncate text-[11px] font-semibold text-white/25">
              À venir
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      title={isCollapsed ? item.label : undefined}
      className={({ isActive }) =>
        `group flex min-h-12 items-center gap-3 rounded-2xl px-3 py-2 text-sm font-bold transition ${
          isCollapsed ? "justify-center" : ""
        } ${
          isActive
            ? "bg-white text-blue-950 shadow-xl shadow-sky-500/20"
            : "text-white/68 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl transition ${
              isActive
                ? "bg-blue-50 text-blue-700"
                : "bg-white/[0.06] text-white/60 group-hover:bg-white/10 group-hover:text-white"
            }`}
          >
            <Icon />
          </span>

          {!isCollapsed && (
            <div className="min-w-0">
              <p className="truncate">{item.label}</p>
              <p
                className={`truncate text-[11px] font-semibold ${
                  isActive ? "text-blue-700/70" : "text-white/35"
                }`}
              >
                {item.description}
              </p>
            </div>
          )}
        </>
      )}
    </NavLink>
  );
}

function AsideMenu() {
  const { isCollapsed, toggleSidebar } = useSidebar();

  const handleToggle = () => {
    if (typeof toggleSidebar === "function") {
      toggleSidebar();
    }
  };

  return (
    <aside
      className={`fixed left-0 top-[88px] z-40 hidden h-[calc(100vh-88px)] flex-col border-r border-white/10 bg-slate-950 text-white shadow-2xl shadow-slate-950/40 transition-all duration-300 lg:flex lg:top-[104px] lg:h-[calc(100vh-104px)] ${
        isCollapsed ? "w-20" : "w-72"
      }`}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-blue-950 via-slate-950 to-slate-950" />
      <div className="absolute inset-x-0 top-0 h-40 bg-sky-500/10 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-40 w-40 rounded-full bg-indigo-500/10 blur-3xl" />

      <div className="relative flex h-full flex-col">
        <div className="border-b border-white/10 p-4">
          <div
            className={`flex items-center gap-3 ${
              isCollapsed ? "justify-center" : "justify-between"
            }`}
          >
            {!isCollapsed && (
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-200/70">
                  Administration
                </p>

                <h2 className="mt-1 font-titre text-xl font-bold text-white">
                  Judo Coach Pro
                </h2>
              </div>
            )}

            <button
              type="button"
              onClick={handleToggle}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-white transition hover:bg-white/15"
              aria-label={isCollapsed ? "Ouvrir le menu" : "Réduire le menu"}
            >
              {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
            </button>
          </div>

          {!isCollapsed && (
            <div className="mt-4 rounded-[1.5rem] border border-white/10 bg-white/10 p-4 backdrop-blur-xl">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/45">
                Espace réservé
              </p>
              <p className="mt-2 text-sm font-bold text-white">
                Pilotage complet du site, des clients, des groupes et des
                données sportives.
              </p>
            </div>
          )}
        </div>

        <nav className="relative flex-1 overflow-y-auto px-3 py-4">
          <div className="space-y-6">
            {MENU_SECTIONS.map((section) => (
              <div key={section.label}>
                {!isCollapsed && (
                  <p className="mb-2 px-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white/30">
                    {section.label}
                  </p>
                )}

                <div className="space-y-1.5">
                  {section.items.map((item) => (
                    <AdminMenuItem
                      key={item.label}
                      item={item}
                      isCollapsed={isCollapsed}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </nav>

        <div className="relative border-t border-white/10 p-4">
          <div
            className={`rounded-[1.5rem] border border-white/10 bg-white/10 p-3 ${
              isCollapsed ? "text-center" : ""
            }`}
          >
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-400/15 text-sky-200">
              <FaUserShield />
            </div>

            {!isCollapsed && (
              <div className="mt-3">
                <p className="text-sm font-bold text-white">Admin principal</p>
                <p className="mt-1 text-xs font-semibold text-white/40">
                  Accès complet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}

export default AsideMenu;
